import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./MenuSection.module.css";

const SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vSHT8e_EYT5SXBsdFt8x0c4U_1uVbVwwlZW7Uhx_3Ot_tbaOyZrV0gBf4HDfcjMqgzG0FHm4KHZoCm4/pub?output=csv";

function parseCSV(text) {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return null;

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));

  const rows = lines.slice(1).map((line) => {
    const cols = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === "," && !inQuotes) {
        cols.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    cols.push(current.trim());

    const row = {};
    headers.forEach((h, i) => {
      row[h] = cols[i] ?? "";
    });
    return row;
  });

  return rows;
}

function buildMenuData(rows) {
  const menuData = {};

  rows.forEach((row) => {
    const category = row.category?.trim();
    const section = row.section?.trim();
    const name = row.name?.trim();
    const price = row.price?.trim();
    const desc = row.desc?.trim();
    const detail = row.detail?.trim();
    const isBoxed = row.isBoxed?.trim().toLowerCase() === "true";
    const subGroup = row.subGroup?.trim();

    if (!category || !section || !name) return;

    if (!menuData[category]) menuData[category] = [];

    let sectionObj = menuData[category].find((s) => s.title === section);
    if (!sectionObj) {
      sectionObj = { title: section, isBoxed, items: isBoxed ? undefined : [], subGroups: isBoxed ? [] : undefined };
      menuData[category].push(sectionObj);
    }

    const item = { name, price, desc: desc || undefined, detail: detail || undefined };

    if (isBoxed && subGroup) {
      if (!sectionObj.subGroups) sectionObj.subGroups = [];
      let group = sectionObj.subGroups.find((g) => g.label === subGroup);
      if (!group) {
        group = { label: subGroup, items: [] };
        sectionObj.subGroups.push(group);
      }
      group.items.push(item);
    } else {
      if (!sectionObj.items) sectionObj.items = [];
      sectionObj.items.push(item);
    }
  });

  return menuData;
}

const MenuSection = () => {
  const [menuData, setMenuData] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const sectionRef = useRef(null);

  const fetchMenu = useCallback(async () => {
    setLoadState("loading");
    try {
      const res = await fetch(SHEET_CSV_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const rows = parseCSV(text);
      if (!rows || rows.length === 0) throw new Error("Empty or malformed sheet");
      const data = buildMenuData(rows);
      if (Object.keys(data).length === 0) throw new Error("No valid menu rows found");
      setMenuData(data);
      setActiveTab(Object.keys(data)[0]);
      setLoadState("success");
    } catch (err) {
      console.error("Menu fetch failed:", err);
      setErrorMsg(err.message);
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  useEffect(() => {
    if (!sectionRef.current || !activeTab) return;
    const elements = sectionRef.current.querySelectorAll("[data-animate]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add(styles.show);
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeTab, menuData]);

  const getStarPosition = () => {
    if (!menuData || !activeTab) return "0%";
    const keys = Object.keys(menuData);
    const index = keys.indexOf(activeTab);
    const totalTabs = keys.length;
    return `${(index * (100 / totalTabs)) + (100 / (totalTabs * 2))}%`;
  };

  if (loadState === "loading") {
    return (
      <section className={styles.wrapper}>
        <div className={styles.container} style={{ textAlign: "center", padding: "4rem 0" }}>
          <p style={{ color: "var(--color-muted, #888)", fontSize: "14px" }}>Loading menu...</p>
        </div>
      </section>
    );
  }

  if (loadState === "error") {
    return (
      <section className={styles.wrapper}>
        <div className={styles.container} style={{ textAlign: "center", padding: "4rem 0" }}>
          <p style={{ color: "#820014", fontSize: "14px", marginBottom: 12 }}>
            Failed to load menu: {errorMsg}
          </p>
          <button onClick={fetchMenu} style={{ fontSize: 13, cursor: "pointer" }}>Retry</button>
        </div>
      </section>
    );
  }

  if (!menuData || !activeTab) return null;

  return (
    <section className={styles.wrapper} ref={sectionRef}>
      <div className={styles.container}>
        <h1 data-animate className={`${styles.mainTitle} ${styles.fadeDown}`}>Menus</h1>

        <div data-animate className={`${styles.tabsWrapper} ${styles.fadeUp}`}>
          <div className={styles.starIndicator} style={{ left: getStarPosition() }}>✦</div>
          <div className={styles.tabs}>
            {Object.keys(menuData).map((tab) => (
              <button
                key={tab}
                className={`${styles.tabBtn} ${activeTab === tab ? styles.activeTab : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className={styles.tabLine}></div>
        </div>

        <div className={styles.menuGrid}>
          {menuData[activeTab].map((section, idx) => (
            <div
              key={`${activeTab}-${idx}`}
              data-animate
              className={`${section.isBoxed ? styles.boxedSection : styles.standardSection} ${styles.fadeUp}`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <h2 className={styles.sectionTitle}>{section.title}</h2>
              {section.subGroups ? (
                section.subGroups.map((group, gIdx) => (
                  <div key={gIdx} className={styles.subGroup}>
                    <h4 className={styles.subLabel}>{group.label}</h4>
                    {group.items.map((item, iIdx) => <MenuItem key={iIdx} {...item} />)}
                  </div>
                ))
              ) : (
                section.items?.map((item, iIdx) => <MenuItem key={iIdx} {...item} />)
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const MenuItem = ({ name, price, desc, detail }) => (
  <div className={styles.menuItem}>
    <div className={styles.itemHeader}>
      <span className={styles.itemName}>{name}</span>
      <span className={styles.itemPrice}>{price}</span>
    </div>
    {desc && <p className={styles.itemDesc}>{desc}</p>}
    {detail && <p className={styles.itemDetail}>{detail}</p>}
  </div>
);

export default MenuSection;